import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { api } from '../api/client'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Table,
} from '@/admin-design/_common'
import type { BadgeVariant } from '@/admin-design/_common'

export default function Jobs() {
  const qc = useQueryClient()
  const [params, setParams] = useSearchParams()
  const selectedJob = Number(params.get('job_id') || 0) || null

  const setSelectedJob = (id: number | null) => {
    const p = new URLSearchParams(params)
    if (id) p.set('job_id', String(id))
    else p.delete('job_id')
    setParams(p)
  }

  const { data } = useQuery({ queryKey: ['jobs'], queryFn: () => api.jobs(100), refetchInterval: 5000 })
  const items: any[] = Array.isArray(data) ? data : []

  const { data: logData } = useQuery({
    queryKey: ['logs', selectedJob],
    queryFn: () => api.logs(1000, selectedJob!),
    enabled: !!selectedJob,
    refetchInterval: 5000,
  })
  const logs: any[] = Array.isArray(logData) ? logData : []

  const cancel = useMutation({
    mutationFn: (id: number) => api.cancelJob(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jobs'] }),
  })

  const retry = useMutation({
    mutationFn: (id: number) => api.retryJob(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['jobs'] })
      qc.invalidateQueries({ queryKey: ['logs'] })
    },
  })

  const cleanup = useMutation({
    mutationFn: (dryRun: boolean) => api.createJob('cleanup_duplicates', { dry_run: dryRun }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jobs'] }),
  })

  const seoOptimize = useMutation({
    mutationFn: () => api.createJob('seo_optimize', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jobs'] }),
  })

  const isCancellable = (s: string) => s === 'queued' || s === 'running'
  const isRetryable = (s: string) => s === 'failed' || s === 'dead' || s === 'cancelled'

  const selectedJobData = items.find((j: any) => j.id === selectedJob)

  // 페이지네이션
  const PAGE_SIZE = 20
  const [page, setPage] = useState(1)
  const totalPages = Math.ceil(items.length / PAGE_SIZE)
  const paged = items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <h1 className="text-2xl font-bold">작업 큐</h1>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="primary"
            onClick={() => { if (confirm('SEO 최적화는 최대 9,999개 상품에 대해 LLM 작업을 실행할 수 있습니다. 계속할까요?')) seoOptimize.mutate() }}
            loading={seoOptimize.isPending}>
            SEO 최적화
          </Button>
          <Button size="sm" variant="secondary"
            onClick={() => { if (confirm('중복 진단 (DRY RUN)')) cleanup.mutate(true) }}
            loading={cleanup.isPending}>
            중복 진단
          </Button>
          <Button size="sm" variant="destructive"
            onClick={() => { if (confirm('중복 상품 정리는 중복된 네이버 판매 목록을 판매중지(SUSPEND)합니다. 계속할까요?')) cleanup.mutate(false) }}
            loading={cleanup.isPending}>
            중복 정리
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* 작업 목록 (왼쪽) */}
        <Card className="overflow-hidden overflow-x-auto">
          <Card.Header className="py-2 px-4">
            <Card.Title className="text-sm">작업 목록</Card.Title>
          </Card.Header>
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.Header>ID</Table.Header>
                <Table.Header>타입</Table.Header>
                <Table.Header>상태</Table.Header>
                <Table.Header>완료</Table.Header>
                <Table.Header className="text-right w-20">동작</Table.Header>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {paged.map((j: any) => (
                <Table.Row
                  key={j.id}
                  className={`cursor-pointer ${selectedJob === j.id ? 'bg-accent' : ''}`}
                  onClick={() => setSelectedJob(selectedJob === j.id ? null : j.id)}
                >
                  <Table.Cell className="font-mono text-xs">#{j.id}</Table.Cell>
                  <Table.Cell className="text-xs">{j.type}</Table.Cell>
                  <Table.Cell>
                    <StatusBadge status={j.status} hasError={!!j.error_message} />
                  </Table.Cell>
                  <Table.Cell className="text-xs text-muted-foreground">{j.finished_at || j.scheduled_at}</Table.Cell>
                  <Table.Cell className="text-right" onClick={(e) => e.stopPropagation()}>
                    {isCancellable(j.status) && (
                      <button onClick={() => { if (confirm(`#${j.id} 취소?`)) cancel.mutate(j.id) }}
                        disabled={cancel.isPending} className="text-destructive hover:underline text-xs">
                        취소
                      </button>
                    )}
                    {isRetryable(j.status) && (
                      <button onClick={() => { if (confirm(`#${j.id} 재시도?`)) retry.mutate(j.id) }}
                        disabled={retry.isPending} className="text-xs text-foreground hover:underline">
                        재시도
                      </button>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
              {items.length === 0 && (
                <Table.Row className="hover:bg-transparent">
                  <Table.Cell colSpan={5} className="p-0">
                    <EmptyState title="작업이 없습니다" />
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 py-2 border-t border-border">
              <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}
                className="text-xs px-2 py-1 rounded hover:bg-accent disabled:opacity-30">
                ←
              </button>
              <span className="text-xs text-muted-foreground">{page} / {totalPages}</span>
              <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages}
                className="text-xs px-2 py-1 rounded hover:bg-accent disabled:opacity-30">
                →
              </button>
            </div>
          )}
        </Card>

        {/* 로그 (오른쪽) */}
        <Card className="overflow-hidden overflow-x-auto">
          <Card.Header className="py-2 px-4">
            <Card.Title className="text-sm flex items-center gap-2">
              {selectedJob ? (
                <>
                  <span>#{selectedJob} 로그</span>
                  {selectedJobData && (
                    <>
                      <Badge variant="muted">{selectedJobData.type}</Badge>
                      <StatusBadge status={selectedJobData.status} hasError={!!selectedJobData.error_message} />
                    </>
                  )}
                </>
              ) : (
                <span className="text-muted-foreground">작업을 선택하세요</span>
              )}
            </Card.Title>
            {selectedJobData?.error_message && (
              <p className="text-xs text-destructive mt-1">{selectedJobData.error_message}</p>
            )}
          </Card.Header>
          {selectedJob ? (
            <div className="max-h-[70vh] overflow-y-auto">
              <Table>
                <Table.Head>
                  <Table.Row>
                    <Table.Header className="w-36">시각</Table.Header>
                    <Table.Header className="w-16">레벨</Table.Header>
                    <Table.Header>메시지</Table.Header>
                  </Table.Row>
                </Table.Head>
                <Table.Body>
                  {logs.map((l: any) => (
                    <Table.Row key={l.id}>
                      <Table.Cell className="text-xs text-muted-foreground font-mono">{l.created_at}</Table.Cell>
                      <Table.Cell><LevelBadge level={l.level} /></Table.Cell>
                      <Table.Cell className="text-xs whitespace-pre-wrap break-all">{l.message}</Table.Cell>
                    </Table.Row>
                  ))}
                  {logs.length === 0 && (
                    <Table.Row className="hover:bg-transparent">
                      <Table.Cell colSpan={3} className="p-0">
                        <EmptyState title="로그가 없습니다" />
                      </Table.Cell>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table>
            </div>
          ) : (
            <div className="py-12">
              <EmptyState title="왼쪽에서 작업을 선택하면 로그가 표시됩니다" />
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}

function StatusBadge({ status, hasError }: { status: string; hasError?: boolean }) {
  if (status === 'done' && hasError) return <Badge variant="warning">일부실패</Badge>
  const map: Record<string, BadgeVariant> = {
    done: 'success', error: 'destructive', failed: 'destructive', dead: 'destructive',
    running: 'info', cancelled: 'muted', queued: 'warning', pending: 'warning',
  }
  return <Badge variant={map[status] ?? 'muted'}>{status}</Badge>
}

function LevelBadge({ level }: { level: string }) {
  const map: Record<string, BadgeVariant> = { error: 'destructive', warn: 'warning' }
  return <Badge variant={map[level] ?? 'muted'}>{level}</Badge>
}
